import React, { useMemo, useState, useRef, useEffect, lazy, Suspense } from 'react';
import './PerimetreSelector.css';
import { usePerimetreReferentiel } from './usePerimetreReferentiel';
import { makeDeptIndex, type ReferentielRegion } from './perimetreCoverage';
import PerimetreMapSkeleton from './PerimetreMapSkeleton';
import Banner from '../Banner/Banner';
import ViewSwitch from '../ViewSwitch/ViewSwitch';

// Invariant : un acheteur doit couvrir au moins un departement. Toute action qui viderait
// completement le perimetre est bloquee (banner danger), jamais persistee.
const MIN_DEPT_ERROR = 'Vous devez conserver au moins un département dans votre périmètre.';

// Carte chargee a la demande (geometrie ~166 Ko) : hors du bundle principal,
// importee seulement quand l'utilisateur ouvre la vue Carte.
const FranceMap = lazy(() => import('./FranceMap'));

// Detail structure du perimetre (aligne sur Organisme::perimetreDetail cote API).
export interface PerimetreRegionValue {
  id: number;
  nom: string;
  code: string;
}
export interface PerimetreDepartementValue {
  id: number;
  nom: string;
  id_region: number;
}
export interface PerimetreValue {
  type: 'national' | 'zones' | null;
  regions: PerimetreRegionValue[];
  departements: PerimetreDepartementValue[];
}

interface PerimetreSelectorProps {
  value: PerimetreValue;
  onChange: (next: PerimetreValue) => void;
  disabled?: boolean;
  // Sauvegarde en cours : la carte affiche un voile clignotant et bloque le clic.
  saving?: boolean;
}

/**
 * Selecteur de perimetre geographique acheteur.
 * National (toute la France) XOR un ensemble de regions entieres et/ou de
 * departements isoles. Une region cochee couvre tous ses departements ; on peut
 * decocher un departement pour la degrader en selection departementale precise.
 * Composant controle : la selection vit dans `value`, chaque interaction emet le
 * nouveau detail via onChange (le parent persiste).
 */
const PerimetreSelector: React.FC<PerimetreSelectorProps> = ({ value, onChange, disabled = false, saving = false }) => {
  const { referentiel, loading, error } = usePerimetreReferentiel();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [view, setView] = useState<'liste' | 'carte'>('carte');
  // Message d'erreur "min 1 departement" (auto-efface apres quelques secondes).
  const [minDeptError, setMinDeptError] = useState<string | null>(null);
  // Etat "vide" LOCAL (apres "Tout deselectionner") : page blanche pour re-choisir, sans persister
  // le vide -> le perimetre enregistre reste le dernier valide tant qu'on n'a pas re-selectionne.
  const [cleared, setCleared] = useState<boolean>(false);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (errorTimer.current) clearTimeout(errorTimer.current); }, []);

  const blockMinDept = () => {
    setMinDeptError(MIN_DEPT_ERROR);
    if (errorTimer.current) clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setMinDeptError(null), 4000);
  };

  // Selection "effective" : type 'national' signifie "toute la France". On l'etend
  // alors a toutes les regions pour l'affichage et les toggles (decocher une zone
  // depuis National repasse automatiquement en selection precise).
  const isNational = !cleared && value.type === 'national';
  const effRegions: PerimetreRegionValue[] = useMemo(
    () => (cleared ? [] : (isNational ? referentiel.map(r => ({ id: r.id, nom: r.nom, code: r.code })) : value.regions)),
    [cleared, isNational, referentiel, value.regions],
  );
  const effDepts: PerimetreDepartementValue[] = cleared ? [] : (isNational ? [] : value.departements);

  const selectedRegionIds = useMemo(() => new Set(effRegions.map(r => r.id)), [effRegions]);
  const selectedDeptByRegion = useMemo(() => {
    const map = new Map<number, Set<number>>();
    effDepts.forEach(d => {
      if (!map.has(d.id_region)) map.set(d.id_region, new Set());
      map.get(d.id_region)!.add(d.id);
    });
    return map;
  }, [effDepts]);

  const totalSelectedDepts = useMemo(() => {
    let n = effDepts.length;
    referentiel.forEach(r => {
      if (selectedRegionIds.has(r.id)) n += r.departements.length;
    });
    return n;
  }, [effDepts.length, referentiel, selectedRegionIds]);

  // Index departement -> sa region (pour piloter la carte, qui ne connait que les codes departement).
  const deptIndex = useMemo(() => makeDeptIndex(referentiel), [referentiel]);

  const deptState = (deptId: number): 'region' | 'isole' | 'libre' => {
    const entry = deptIndex.get(deptId);
    if (!entry) return 'libre';
    if (selectedRegionIds.has(entry.region.id)) return 'region';
    return selectedDeptByRegion.get(entry.region.id)?.has(deptId) ? 'isole' : 'libre';
  };

  // Emet en normalisant : si toutes les regions sont couvertes (sans dept isole),
  // c'est National ; sinon zones precises. Garde-fou : un resultat a zero couverture
  // (aucune region ET aucun departement) est interdit -> banner danger, pas d'emission.
  const emitNormalized = (regions: PerimetreRegionValue[], departements: PerimetreDepartementValue[]) => {
    const isNationalResult = referentiel.length > 0 && regions.length === referentiel.length && departements.length === 0;
    if (!isNationalResult && regions.length === 0 && departements.length === 0) {
      blockMinDept();
      return;
    }
    setMinDeptError(null);
    setCleared(false); // une selection valide a ete faite -> on sort de l'etat "vide"
    if (isNationalResult) {
      onChange({ type: 'national', regions: [], departements: [] });
    } else {
      onChange({ type: 'zones', regions, departements });
    }
  };

  const toggleDeptById = (deptId: number) => {
    const entry = deptIndex.get(deptId);
    if (entry) toggleDept(entry.region, entry.dept);
  };

  const selectAll = () => { if (!disabled) { setMinDeptError(null); setCleared(false); onChange({ type: 'national', regions: [], departements: [] }); } };
  // "Tout deselectionner" : vide l'affichage LOCALEMENT (page blanche pour re-choisir) sans persister.
  // Le perimetre enregistre reste inchange tant qu'on n'a pas re-selectionne au moins une zone.
  const clearAll = () => { if (!disabled) { setMinDeptError(null); setCleared(true); } };

  const nothingSelected = effRegions.length === 0 && effDepts.length === 0;

  const toggleExpand = (regionId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(regionId) ? next.delete(regionId) : next.add(regionId);
      return next;
    });
  };

  const regionState = (region: ReferentielRegion): 'full' | 'partial' | 'none' => {
    if (selectedRegionIds.has(region.id)) return 'full';
    const sel = selectedDeptByRegion.get(region.id);
    return sel && sel.size > 0 ? 'partial' : 'none';
  };

  const toggleRegion = (region: ReferentielRegion) => {
    if (disabled) return;
    if (selectedRegionIds.has(region.id)) {
      emitNormalized(effRegions.filter(r => r.id !== region.id), effDepts);
    } else {
      emitNormalized(
        [...effRegions, { id: region.id, nom: region.nom, code: region.code }],
        effDepts.filter(d => d.id_region !== region.id),
      );
    }
  };

  const toggleDept = (region: ReferentielRegion, dept: { id: number; nom: string }) => {
    if (disabled) return;
    const otherRegionsDepts = effDepts.filter(d => d.id_region !== region.id);

    if (selectedRegionIds.has(region.id)) {
      // Region pleine : decocher un departement la degrade en selection precise.
      const reste = region.departements
        .filter(d => d.id !== dept.id)
        .map(d => ({ id: d.id, nom: d.nom, id_region: region.id }));
      emitNormalized(effRegions.filter(r => r.id !== region.id), [...otherRegionsDepts, ...reste]);
      return;
    }

    const sel = new Set(selectedDeptByRegion.get(region.id) ?? []);
    sel.has(dept.id) ? sel.delete(dept.id) : sel.add(dept.id);

    if (sel.size === region.departements.length && region.departements.length > 0) {
      // Tous les departements coches -> on promeut en region pleine.
      emitNormalized([...effRegions, { id: region.id, nom: region.nom, code: region.code }], otherRegionsDepts);
      return;
    }

    const regionDepts = region.departements
      .filter(d => sel.has(d.id))
      .map(d => ({ id: d.id, nom: d.nom, id_region: region.id }));
    emitNormalized(effRegions, [...otherRegionsDepts, ...regionDepts]);
  };

  const removeRegion = (regionId: number) => {
    if (disabled) return;
    emitNormalized(effRegions.filter(r => r.id !== regionId), effDepts);
  };

  const removeDept = (deptId: number) => {
    if (disabled) return;
    emitNormalized(effRegions, effDepts.filter(d => d.id !== deptId));
  };

  if (loading) {
    return <div className="perimetre-selector"><PerimetreMapSkeleton /></div>;
  }
  if (error) {
    return <div className="perimetre-selector perimetre-selector--error">{error}</div>;
  }

  return (
    <div className={`perimetre-selector${disabled ? ' is-disabled' : ''}`}>
      {minDeptError && (
        <Banner variant="danger" filled message={minDeptError} className="perimetre-min-dept-error" />
      )}
      {cleared && (
        <Banner
          variant="info"
          message="Périmètre vidé : sélectionnez au moins une zone pour enregistrer. Tant que rien n'est choisi, votre périmètre actuel reste inchangé."
          className="perimetre-cleared-hint"
        />
      )}
      <div className="perimetre-zones">
          {isNational ? (
            <div className="perimetre-selection">
              <div className="perimetre-selection-tags">
                <span className="perimetre-tag perimetre-tag--region perimetre-tag--national">
                  National
                  {!disabled && (
                    <button type="button" className="perimetre-tag-remove" onClick={clearAll} aria-label="Tout désélectionner">×</button>
                  )}
                </span>
              </div>
              <span className="perimetre-selection-count">{totalSelectedDepts} départements couverts</span>
            </div>
          ) : nothingSelected ? (
            <span className="perimetre-empty">Aucune zone sélectionnée — sélectionnez des régions/départements, ou « Tout sélectionner » pour couvrir toute la France (National).</span>
          ) : (
            <div className="perimetre-selection">
              <div className="perimetre-selection-tags">
                {effRegions.map(r => (
                  <span key={`sr${r.id}`} className="perimetre-tag perimetre-tag--region">
                    {r.id} - {r.nom}
                    {!disabled && (
                      <button type="button" className="perimetre-tag-remove" onClick={() => removeRegion(r.id)} aria-label={`Retirer ${r.nom}`}>×</button>
                    )}
                  </span>
                ))}
                {effDepts.map(d => (
                  <span key={`sd${d.id}`} className="perimetre-tag">
                    {d.id} - {d.nom}
                    {!disabled && (
                      <button type="button" className="perimetre-tag-remove" onClick={() => removeDept(d.id)} aria-label={`Retirer ${d.nom}`}>×</button>
                    )}
                  </span>
                ))}
              </div>
              <span className="perimetre-selection-count">{totalSelectedDepts} departement{totalSelectedDepts > 1 ? 's' : ''} couvert{totalSelectedDepts > 1 ? 's' : ''}</span>
            </div>
          )}

          <div className="perimetre-toolbar">
            <ViewSwitch
              value={view}
              options={[{ value: 'carte', label: 'Carte' }, { value: 'liste', label: 'Liste' }]}
              onChange={setView}
              ariaLabel="Affichage du périmètre"
            />
            <div className="perimetre-quick-actions">
              <button
                type="button"
                className="perimetre-quick-btn perimetre-quick-btn--national"
                onClick={selectAll}
                disabled={disabled || isNational}
                title="Couvrir toute la France (National)"
              >Tout sélectionner</button>
              <button
                type="button"
                className="perimetre-quick-btn"
                onClick={clearAll}
                disabled={disabled || nothingSelected}
              >Tout désélectionner</button>
            </div>
          </div>

          {view === 'liste' ? (
          <ul className="perimetre-region-list">
            {referentiel.map(region => {
              const state = regionState(region);
              const isOpen = expanded.has(region.id);
              const selCount = selectedRegionIds.has(region.id)
                ? region.departements.length
                : (selectedDeptByRegion.get(region.id)?.size ?? 0);
              return (
                <li key={region.id} className={`perimetre-region perimetre-region--${state}`}>
                  <div className="perimetre-region-row">
                    <label className="perimetre-region-check">
                      <input
                        type="checkbox"
                        checked={state === 'full'}
                        ref={el => { if (el) el.indeterminate = state === 'partial'; }}
                        onChange={() => toggleRegion(region)}
                        disabled={disabled}
                      />
                      <span className="perimetre-region-nom"><span className="perimetre-region-code">{region.id}</span> - {region.nom}</span>
                    </label>
                    <span className="perimetre-region-count">
                      {selCount > 0 ? `${selCount}/${region.departements.length}` : `${region.departements.length} dep.`}
                    </span>
                    <button
                      type="button"
                      className={`perimetre-region-toggle${isOpen ? ' is-open' : ''}`}
                      onClick={() => toggleExpand(region.id)}
                      aria-label={isOpen ? 'Replier' : 'Deplier'}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>

                  {isOpen && (
                    <ul className="perimetre-dept-list">
                      {region.departements.map(dept => {
                        const checked = selectedRegionIds.has(region.id)
                          || (selectedDeptByRegion.get(region.id)?.has(dept.id) ?? false);
                        return (
                          <li key={dept.id} className="perimetre-dept">
                            <label className="perimetre-dept-check">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleDept(region, dept)}
                                disabled={disabled}
                              />
                              <span className="perimetre-dept-nom"><span className="perimetre-dept-code">{dept.id}</span> - {dept.nom}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          ) : (
            <Suspense fallback={<PerimetreMapSkeleton />}>
              <FranceMap
                deptState={deptState}
                onToggleDept={toggleDeptById}
                disabled={disabled}
                saving={saving}
              />
            </Suspense>
          )}
        </div>
    </div>
  );
};

export default PerimetreSelector;
